// The rating weights setting (D73): how much the results and cost averages count in a
// workflow's overall rating. Anyone with ViewWorkflows reads them; ManageRatingWeights edits.
// The form checks the API's rule as you type (each 0–1, summing to 1) and the API checks again.
import { ArrowCounterClockwise } from "@phosphor-icons/react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { type FormEvent, useState } from "react";
import { Button, Input } from "rizzui";

import { usePermissions } from "../auth/permissions";
import { formatNumber, useT } from "../i18n";
import { toast } from "../layout/toast";
import { ago, shortId } from "../pages/format";
import { WidgetCard } from "../ui/Card";
import { ErrorState, Loading } from "../ui/states";
import { Pill } from "../ui/status";
import { ratingWeightsQuery, saveRatingWeights } from "./api";
import { DEFAULT_WEIGHTS, formatRating, RATING_KINDS, type RatingWeights, weigh } from "./model";
import { checkWeights, type WeightsInput, weightsInput } from "./weights";

function WeightsForm({ current, editable }: { current: RatingWeights; editable: boolean }) {
  const t = useT().ratings.weights;
  const queryClient = useQueryClient();
  const [input, setInput] = useState<WeightsInput>(() => weightsInput(current.weights));
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string>();
  const check = checkWeights(input);
  const shown = check.ok ? check.weights : current.weights;
  const labels = { results: [t.results, t.resultsHint], cost: [t.cost, t.costHint] } as const;

  async function submit(e: FormEvent) {
    e.preventDefault();
    if (!check.ok) return;
    setBusy(true);
    setError(undefined);
    try {
      const saved = await saveRatingWeights(check.weights);
      queryClient.setQueryData(ratingWeightsQuery().queryKey, saved);
      await queryClient.invalidateQueries({ queryKey: ["catalog", "workflows"] });
      toast(t.saved);
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setBusy(false);
    }
  }

  return (
    <form className="space-y-4" aria-label={t.title} onSubmit={(e) => void submit(e)}>
      <div className="grid gap-4 sm:grid-cols-2">
        {RATING_KINDS.map((kind) => (
          <Input
            key={kind}
            label={labels[kind][0]}
            helperText={labels[kind][1]}
            inputMode="decimal"
            value={input[kind]}
            disabled={!editable}
            error={!check.ok && check.problem === "weight" && check.kind === kind ? t.badWeight : undefined}
            onChange={(e) => setInput({ ...input, [kind]: e.target.value })}
          />
        ))}
      </div>
      <p className="m-0 font-mono text-[13px] text-gray-700">{t.formula(formatNumber(shown.results), formatNumber(shown.cost))}</p>
      <p className="m-0 text-[13px] text-gray-500">{t.example(formatRating(weigh({ results: 4, cost: 5 }, shown)))}</p>
      {check.ok ? <p className="m-0 text-[13px] text-green-dark">{t.sum(formatNumber(1))}</p> : null}
      {!check.ok && check.problem === "sum" ? (
        <p className="m-0 text-[13px] text-red-dark" role="alert">
          {t.mustSum(formatNumber(check.sum, { maximumFractionDigits: 6 }))}
        </p>
      ) : null}
      {error ? <ErrorState message={error} /> : null}
      {editable ? (
        <div className="flex flex-wrap justify-end gap-3 border-t border-muted pt-4">
          <Button variant="outline" className="gap-1.5" onClick={() => setInput(weightsInput(DEFAULT_WEIGHTS))} disabled={busy}>
            <ArrowCounterClockwise className="h-4 w-4" /> {t.reset}
          </Button>
          <Button type="submit" isLoading={busy} disabled={!check.ok}>
            {t.save}
          </Button>
        </div>
      ) : (
        <p className="m-0 text-[13px] text-gray-500">{t.readOnly}</p>
      )}
    </form>
  );
}

export function WeightsCard() {
  const t = useT().ratings.weights;
  const { can } = usePermissions();
  const query = useQuery(ratingWeightsQuery());
  const w = query.data;
  return (
    <WidgetCard
      title={t.title}
      description={t.description}
      action={
        w?.isDefault ? (
          <Pill tone="gray">{t.defaults}</Pill>
        ) : w?.updatedBy && w.updatedAt ? (
          <span className="text-xs text-gray-500">{t.setBy(shortId(w.updatedBy), ago(w.updatedAt))}</span>
        ) : undefined
      }
    >
      <p className="mb-4 mt-0 max-w-prose text-[13.5px] text-gray-700">{t.explanation}</p>
      {query.isPending ? <Loading /> : null}
      {query.isError ? <ErrorState title={t.loadError} message={query.error.message} /> : null}
      {w ? <WeightsForm key={w.updatedAt ?? 0} current={w} editable={can("ManageRatingWeights")} /> : null}
    </WidgetCard>
  );
}
